/* eslint-disable no-underscore-dangle */
import lod_, { set } from "lodash";
import Joi from "joi";
import csvDownload from "json-to-csv-export";
import { read, utils } from "xlsx";
import {
	Badge,
	Card,
	CardContent,
	Divider,
	Icon,
	IconButton,
	Menu,
	MenuItem,
	Tooltip
} from "@mui/material";
import { useLanguages } from "hooks/useLanguages";
import MDBox from "components/Basics/MDBox";
import MDTypography from "components/Basics/MDTypography";
import DefaultDataTable from "components/Custom/Tables/DefaultDataTable";
import { useEffect, useRef, useState } from "react";
import { useDispatch } from "react-redux";
import ConfirmDialogButton from "components/Custom/Dialogs/ConfirmDialogButton";
import MDButton from "components/Basics/MDButton";
import { display, hide } from "redux-react/reducers/snackBarReducer";
import csvTemplate from "assets/template/template_theme_faibrik.csv";
import { v4 } from "uuid";
import { useTranslation } from "react-i18next";
import LanguagesHelp from "./LanguagesHelp";
import LanguagesCell from "./LanguagesCell";
import { useFormActions } from "hooks/useFormActions";
import MenuLanguages from "../components/MenuLanguages";
import ReactThemeActions from "redux-react/actions/themeActions";

/**
 * Theme Card component
 * Displays a card with theme information and allows editing and deleting themes.
 * @param {Object} props - Component properties
 * @param {Array} props.folders - List of folders
 * @param {Object} props.theme - Theme object to display
 * @param {Function} props.onEdit - Function to call when editing a theme
 * @param {Function} props.handleDeleteFolder - Function to call when deleting a theme
 * @param {Function} props.getFolders - Function to fetch folders (optional)
 * @returns {JSX.Element} Rendered ThemeCard component
 */
const ThemeCard = ({
	folders,
	theme,
	onEdit,
	handleDeleteFolder,
	getFolders = () => {},
	onAddFile = () => {},
	themeActions
}) => {
	const dispatch = useDispatch();
	const { languages } = useLanguages();
	const { startCategoryTraining, startCategoryUntraining, translateV2Dataset } = themeActions;
	const { updateItem, getItemsFromCollection, deleteItems } = useFormActions();

	const [selectedRows, setSelectedRows] = useState([]);
	const [categories, setCategories] = useState([]);

	const [menuRef, setMenuRef] = useState(null);
	const [menuValues, setMenuValues] = useState({});
	const fileInputRef = useRef(null);
	const [anchorElMenu, setAnchorElMenu] = useState(null);
	const [languageMenuAnchorEl, setLanguageMenuAnchorEl] = useState(null);

	const { t, ready } = useTranslation(); // Hook to access translations
	const menuRefTrigger = useRef();

	/**
	 * Convert input categories to a format suitable for CSV export
	 * @param {Array} inputCategories - Array of categories with questions and answers
	 * @returns {Array} Transformed data ready for CSV export
	 */
	function transformData(inputCategories) {
		const result = [];

		inputCategories.forEach(category => {
			const languages = Object.keys(category.label);

			languages.forEach(lang => {
				const categoryLabel = category.label[lang]?.trim() || "";

				category.questions.forEach(question => {
					const questionLabel = question.question.label[lang]?.trim() || "";
					const answerLabel = question.answer.label[lang]?.trim() || "";

					result.push({
						language: lang,
						category_id: category.code,
						question_id: question.code,
						category: categoryLabel,
						question: questionLabel,
						answer: answerLabel
					});
				});
			});
		});

		return result;
	}

	/**
	 * Fetch the theme tree and download it as a CSV file
	 */
	const getTreeTheme = () => {
		dispatch(
			ReactThemeActions.getTreeTheme(theme?.code, theme => {
				if (!lod_.isNil(theme)) {
					const csvData = transformData(theme.categories);
					csvDownload({
						data: csvData,
						filename: `${theme.name}.csv`,
						delimiter: ";"
					});
				}
			})
		);
	};

	/**
	 * Fetch categories for the theme
	 */
	const getCategories = async () => {
		const fetchedCategories = await getItemsFromCollection("answerItem", {
			query: {
				documentType: "answerType",
				type: "themeCategory",
				parent: theme?.code
			},
			sort: {
				name: 1
			}
		});

		setCategories(fetchedCategories);
	};

	/**
	 * Handle file import - supports CSV, XLSX and other table formats
	 * @param {Event} event - File input change event
	 */
	const handleFileImport = event => {
		const file = event.target.files[0];
		if (!file) return;

		const reader = new FileReader();
		reader.onload = e => {
			try {
				const workbook = read(e.target.result, { type: "binary", codepage: 65001 });
				// Get the first worksheet
				const worksheetName = workbook.SheetNames[0];
				const worksheet = workbook.Sheets[worksheetName];

				// Import all fields as strings
				const jsonWithHeaders = utils.sheet_to_json(worksheet, {
					raw: false, // Don't use raw values, convert everything to strings
					defval: "", // Default value for empty cells
					blankrows: false // Skip blank rows
				});

				dispatch(
					ReactThemeActions.insertTreeFromJSON(
						{
							code: theme.code,
							documents: jsonWithHeaders
						},
						() => {
							getFolders();
						},
						() => {
							getFolders();
							dispatch(
								display({
									message: t("ASSETS.THEME.cannotImportCSV"),
									type: "error"
								})
							);
						}
					)
				);
			} catch {
				display({
					message: t("ASSETS.THEME.cannotImportCSV"),
					type: "error"
				});
			}
		};

		reader.readAsArrayBuffer(file);
		// Reset the input value to allow importing the same file again
		event.target.value = "";
	};

	/**
	 * Trigger file input click
	 */
	const triggerFileImport = () => {
		if (fileInputRef.current) {
			fileInputRef.current.click();
		}
	};

	/**
	 * Download the CSV template file for users to understand the expected format
	 */
	const downloadTemplate = () => {
		// Create a link to download the template file from assets
		const link = document.createElement("a");
		link.href = csvTemplate;
		link.download = "template_theme_faibrik.csv";
		document.body.appendChild(link);
		link.click();
		document.body.removeChild(link);
	};

	/**
	 * Translate all categories names from a theme
	 */
	const translateCategoryName = async (ids, sourceLanguageCode) => {
		const TYPE = "themeCategory";

		let categories = await getItemsFromCollection("answerItem", {
			query: {
				documentType: "answerType",
				type: TYPE,
				_id: {
					$in: ids
				}
			},
			projection: {
				_id: 1,
				code: 1,
				content: 1
			}
		});

		categories = (categories || [])
			.map(category => ({
				code: category._id,
				content: category?.content[0] || null
			}))
			.filter(category => !lod_.isNil(category.content));

		const callback = async ({ code, result, success }) => {
			if (success === true) {
				// Update
				const data = {
					values: {
						content: [result]
					},
					target: "answerItem"
				};
				await updateItem(
					code,
					data,
					() => {
						getCategories();
					},
					() => {
						dispatch(
							display({
								message: t("ASSETS.THEME.categoryUpdateError", {
									defaultValue: "Impossible de mettre à jour la catégorie"
								}),
								type: "error"
							})
						);
					}
				);
			} else {
				dispatch(
					display({
						message: t("ASSETS.THEME.translationError", {
							defaultValue:
								"Impossible de traduire la catégorie, vérifiez que la langue source ne soit pas vide"
						}),
						type: "error",
						validation: (
							<MDButton
								color="error"
								variant="outlined"
								sx={{ ml: 1 }}
								onClick={() => {
									dispatch(hide());
								}}
							>
								{t("SETTINGS.close")}
							</MDButton>
						)
					})
				);
			}
		};

		const dataset = lod_
			.chain(categories)
			.map(element => {
				const { code, content } = element;

				const entryObject = {
					[sourceLanguageCode]:
						lod_.get(content, sourceLanguageCode?.toLowerCase()) ||
						lod_.get(content, sourceLanguageCode?.toUpperCase())
				};

				return {
					code,
					entryObject,
					sourceLanguage: sourceLanguageCode,
					callback
				};
			})
			.value();

		const inputSchemaValue = Joi.object().pattern(
			Joi.string().trim().required().description("Code langue").empty(),
			Joi.object({
				text: Joi.string().trim().required().description("Texte").min(1).empty()
			})
		);
		const inputSchemaValueString = `Joi.object().pattern(
			Joi.string().trim().required().description("Code langue").empty(),
			Joi.object({
				text: Joi.string().trim().required().description("Texte").min(1).empty()
			})
		)
		`;

		translateV2Dataset(inputSchemaValue, inputSchemaValueString, dataset, () => {});
	};

	/**
	 * Get answer items by type and parent codes
	 * @param {string} type
	 * @param {array} parentCodes
	 * @returns {Promise<Array>}
	 */
	const getAnswerItems = async (type, parentCodes) => {
		return getItemsFromCollection("answerItem", {
			query: {
				documentType: "answerType",
				type,
				parent: {
					$in: parentCodes
				}
			},
			projection: {
				_id: 1,
				content: 1,
				code: 1
			}
		});
	};

	/**
	 * Translate all questions from a theme
	 */
	const translateQuestions = async (codes, sourceLanguageCode) => {
		const TYPE = "themeQuestion";
		let questions = await getAnswerItems(TYPE, codes);

		questions = (questions || [])
			.map(question => ({
				code: question._id,
				content: question?.content[0] || null
			}))
			.filter(question => !lod_.isNil(question.content));

		const callback = async ({ code, result, success }) => {
			if (success === true) {
				// Update
				const data = {
					values: {
						content: [result]
					},
					target: "answerItem"
				};
				await updateItem(
					code,
					data,
					() => {},
					() => {
						dispatch(
							display({
								message: t("ASSETS.THEME.questionUpdateError", {
									defaultValue: "Impossible de mettre à jour la question"
								}),
								type: "error"
							})
						);
					}
				);
			} else {
				dispatch(
					display({
						message: t("ASSETS.THEME.translationQuestionError", {
							defaultValue:
								"Impossible de traduire la question, vérifiez que la langue source ne soit pas vide"
						}),
						type: "error",
						validation: (
							<MDButton
								color="error"
								variant="outlined"
								sx={{ ml: 1 }}
								onClick={() => {
									dispatch(hide());
								}}
							>
								{t("SETTINGS.close")}
							</MDButton>
						)
					})
				);
			}
		};

		const dataset = lod_
			.chain(questions)
			.map(element => {
				const { code, content } = element;

				const entryObject = {
					[sourceLanguageCode]:
						lod_.get(content, sourceLanguageCode?.toLowerCase()) ||
						lod_.get(content, sourceLanguageCode?.toUpperCase())
				};

				return {
					code,
					entryObject,
					sourceLanguage: sourceLanguageCode,
					callback
				};
			})
			.value();

		const inputSchemaValue = Joi.object().pattern(
			Joi.string().trim().required().description("Code langue").empty(),
			Joi.object({
				question: Joi.string().trim().required().description("Question").min(1).empty(),
				answer: Joi.string().trim().required().description("Réponse").min(1).empty()
			})
		);
		const inputSchemaValueString = `Joi.object().pattern(
			Joi.string().trim().required().description("Code langue").empty(),
			Joi.object({
				question: Joi.string().trim().required().description("Question").min(1).empty(),
				answer: Joi.string().trim().required().description("Réponse").min(1).empty()
			})
		)`;

		translateV2Dataset(inputSchemaValue, inputSchemaValueString, dataset, () => {});
	};

	const HEADER_ACTIONS = [
		<MDBox>
			<Badge
				badgeContent={selectedRows.length}
				color="error"
				anchorOrigin={{
					vertical: "top",
					horizontal: "right"
				}}
			>
				<MDButton
					sx={{ ml: 0.5 }}
					variant="contained"
					color="dark"
					onClick={e => {
						setAnchorElMenu(e.currentTarget);
					}}
				>
					<Icon>list_alt</Icon>&nbsp;Actions
				</MDButton>
			</Badge>
			{/* Hidden file input - placed outside menu so it stays mounted */}
			<input
				ref={fileInputRef}
				type="file"
				accept=".csv,.xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel,text/csv"
				onChange={handleFileImport}
				style={{ display: "none" }}
			/>
			{/* Menu */}
			<Menu
				anchorEl={anchorElMenu}
				open={Boolean(anchorElMenu)}
				onClose={() => setAnchorElMenu(null)}
				anchorOrigin={{
					vertical: "bottom",
					horizontal: "center"
				}}
				transformOrigin={{
					vertical: "top",
					horizontal: "center"
				}}
			>
				{/* Actions list */}
				<MenuItem disabled style={{ opacity: 1 }}>
					<MDTypography variant="caption">{t("ASSETS.THEME.actionList")}</MDTypography>
				</MenuItem>

				{/* New category */}
				<MenuItem
					onClick={() => {
						onAddFile(theme);
						setAnchorElMenu(null);
					}}
				>
					<Icon fontSize="medium">add</Icon> {t("ASSETS.THEME.createCategory")}
				</MenuItem>

				{/* Export CSV theme */}
				<Tooltip title={t("ASSETS.THEME.CATEGORY.downloadCSVTooltip")} placement="left">
					<MenuItem onClick={getTreeTheme}>
						<Icon fontSize="medium">download</Icon> {t("SETTINGS.export")}
					</MenuItem>
				</Tooltip>

				{/* Import CSV file - Button */}
				<ConfirmDialogButton
					onCancel={() => {
						setAnchorElMenu(null);
					}}
					onConfirm={(e, values) => {
						triggerFileImport();
						setAnchorElMenu(null);
					}}
					component={
						<Tooltip title={t("ASSETS.THEME.CATEGORY.uploadFileTooltip")} placement="left">
							<MenuItem>
								<Icon fontSize="medium">upload_file</Icon> {t("SETTINGS.import")}
							</MenuItem>
						</Tooltip>
					}
					title={t("ASSETS.THEME.importFileTitle")}
					content={t("ASSETS.THEME.importFileContent")}
				/>

				<Tooltip title={t("ASSETS.THEME.CATEGORY.downloadTemplateTooltip")} placement="left">
					<MenuItem onClick={downloadTemplate}>
						<Icon fontSize="medium">description</Icon> {t("ASSETS.THEME.downloadTemplate")}
					</MenuItem>
				</Tooltip>

				<Divider />

				{/* Actions selection */}
				<MenuItem disabled style={{ opacity: 1 }}>
					<MDTypography variant="caption">{t("ASSETS.THEME.actionSelection")}</MDTypography>
				</MenuItem>

				{/* Translate name */}
				<MenuItem
					onClick={e => {
						const ids = selectedRows.map(row => row._id);
						setLanguageMenuAnchorEl({
							currentTarget: e.currentTarget,
							cb: code => {
								setSelectedRows([]);
								setAnchorElMenu(null);
								translateCategoryName(ids, code);
							}
						});
					}}
					disabled={selectedRows.length < 1}
				>
					<Icon fontSize="medium">translate</Icon>
					{t("ASSETS.THEME.ACTIONS.translateCategoriesName")}
				</MenuItem>
				{/* Translate content */}
				<MenuItem
					onClick={e => {
						const codes = selectedRows.map(row => row.code);
						setLanguageMenuAnchorEl({
							currentTarget: e.currentTarget,
							cb: code => {
								setSelectedRows([]);
								setAnchorElMenu(null);
								translateQuestions(codes, code);
							}
						});
					}}
					disabled={selectedRows.length < 1}
				>
					<Icon fontSize="medium">translate</Icon> {t("ASSETS.THEME.ACTIONS.translateAllQR")}
				</MenuItem>
				{/* Train category dialog */}
				<ConfirmDialogButton
					onConfirm={(e, values) => {
						const codes = selectedRows.map(row => row.code);

						const id = v4();

						// Display a message to the user
						dispatch(
							display({
								id,
								message: t("ASSETS.TRAIN.starting"),
								type: "loading"
							})
						);

						startCategoryTraining(codes, "category", id);

						setAnchorElMenu(null);
					}}
					component={
						<MenuItem disabled={selectedRows.length < 1}>
							<Icon fontSize="medium" sx={{ mr: 1 }}>
								model_training
							</Icon>
							{t("ASSETS.THEME.ACTIONS.trainAllQR")}
						</MenuItem>
					}
					title={t("ASSETS.THEME.startTrainingCategoryTitle")}
					content={t("ASSETS.THEME.startTrainingCategoryDescription")}
				/>

				{/* Untrain category dialog */}
				<ConfirmDialogButton
					onConfirm={(e, values) => {
						const codes = selectedRows.map(row => row.code);

						dispatch(
							display({
								message: t("ASSETS.THEME.questionsUntrainingStarted"),
								type: "info"
							})
						);

						startCategoryUntraining(
							codes,
							"category",
							() => {
								dispatch(
									display({
										message: t("ASSETS.THEME.questionsUntrainingFinish"),
										type: "success"
									})
								);
							},
							() => {
								dispatch(
									display({
										message: t("ASSETS.THEME.untrainingError"),
										type: "error"
									})
								);
							}
						);

						setAnchorElMenu(null);
					}}
					component={
						<MenuItem style={{ color: "red" }} disabled={selectedRows.length < 1}>
							<Icon fontSize="medium" sx={{ mr: 1 }}>
								model_training
							</Icon>
							{t("ASSETS.THEME.ACTIONS.untrainAllQR")}
						</MenuItem>
					}
					title={t("ASSETS.THEME.startUntrainingCategoryTitle")}
					content={t("ASSETS.THEME.startUntrainingCategoryDescription")}
				/>

				{/* Delete */}
				<ConfirmDialogButton
					disabled={selectedRows.length < 1}
					onConfirm={async (e, values) => {
						// onDeleteFolder(folder);

						const ids = selectedRows.map(row => row._id);
						await deleteItems(
							ids,
							"answerItem",
							{
								delete: true,
								actions: [
									"deleteChildAnswerType", // Answer type cave have childs with parent = answerItem.code, delete theme.
									"emptyCacheTheme" // Empty cache for theme
								]
							},
							() => {
								getCategories();
							}
						);

						setSelectedRows([]);
						setAnchorElMenu(null);
					}}
					component={
						<MenuItem
							disabled={selectedRows.length < 1}
							style={{
								color: "red"
							}}
						>
							<Icon fontSize="medium">delete</Icon>
							{t("SETTINGS.delete")}
						</MenuItem>
					}
					title={t("ASSETS.DELETE_CONFIRMATION.title")}
					content={t("ASSETS.THEME.confirmDeleteCategories")}
				/>
			</Menu>
		</MDBox>
	];

	// Force menu position update when translations are ready
	useEffect(() => {
		if (ready && menuRefTrigger.current && Boolean(menuRef)) {
			// Small delay to ensure DOM is updated
			const timer = setTimeout(() => {
				if (menuRefTrigger.current) {
					menuRefTrigger.current.updatePosition();
				}
			}, 0);
			return () => clearTimeout(timer);
		}
		return undefined;
	}, [ready, menuRef, t]);

	useEffect(() => {
		getCategories();
	}, [folders, theme]);

	if (lod_.isNil(theme)) {
		return null;
	}

	return (
		<MDBox
			sx={{
				height: "100%"
			}}
		>
			{/* Menu language */}
			<MenuLanguages
				anchor={languageMenuAnchorEl?.currentTarget}
				onClose={() => {
					setLanguageMenuAnchorEl({});
				}}
				languages={languages}
				onClick={code => {
					setLanguageMenuAnchorEl({});
					languageMenuAnchorEl?.cb(code);
				}}
			/>
			<Card
				sx={{
					height: "100%",
					borderRadius: "0px"
				}}
			>
				<CardContent sx={{ m: 0, p: 0, display: "flex", overflow: "auto" }}>
					<MDBox
						display="flex"
						flexDirection="column"
						sx={{
							flex: 1
						}}
					>
						<MDBox display="flex" justifyContent="space-between" alignItems="center">
							<MDBox>
								<MDTypography variant="h5" sx={{ ml: 3, mt: 3 }}>
									{theme.name}
								</MDTypography>
								<MDTypography variant="subtitle2" sx={{ ml: 3 }}>
									{t("ASSETS.THEME.categoriesList")}
								</MDTypography>
							</MDBox>
						</MDBox>

						<MDBox
							sx={{
								overflow: "auto"
							}}
						>
							<DefaultDataTable
								// Checkbox
								checkbox
								selectedRows={selectedRows}
								handleSelection={(newValues, original) => {
									if (lod_.isNil(original)) {
										// Orignal null = we trigger the column checkbox
										if (!lod_.isEmpty(selectedRows)) {
											setSelectedRows([]);
										} else {
											setSelectedRows(categories);
										}
									} else {
										setSelectedRows(newValues);
									}
								}}
								isSelectedRow={row => {
									return selectedRows.some(selectedRow => selectedRow?.code === row?.code);
								}}
								list={null}
								pagination={{
									defaultEntrie: 50,
									entriesPerPage: [25, 50, 76, 100]
								}}
								noMargin
								noHeader
								headerAction={HEADER_ACTIONS}
								filters={{}}
								canSearch
								table={{
									rows: categories,
									columns: [
										{ accessor: "_id", Header: "id" },
										{ accessor: "code", Header: "code" },
										{ accessor: "name", Header: t("SETTINGS.PAGES.name") },
										{ accessor: "documentType", Header: "documentType" },
										{ accessor: "type", Header: "type" },
										{
											accessor: "languages",
											Header: t("ASSETS.THEME.languages"),
											Cell: LanguagesCell,
											Help: LanguagesHelp
										}
									],
									count: categories.length
								}}
								display={["name", "languages"]}
								compact
								actions={[
									<IconButton
										handleclick={({ values }, e) => {
											setMenuValues(values);
											setMenuRef(e.currentTarget);
											e.stopPropagation();
											e.preventDefault();
										}}
									>
										<Icon fontSize="medium">more_vert</Icon>
									</IconButton>
								]}
							/>
						</MDBox>
						{/* Menu actions selection */}
						<Menu
							open={Boolean(menuRef)}
							anchorEl={menuRef}
							onClose={() => {
								setMenuRef(null);
							}}
							action={menuRefTrigger}
						>
							{/* Options */}
							<MenuItem disabled style={{ opacity: 1 }}>
								<MDTypography variant="caption">{t("SETTINGS.edition")}</MDTypography>
							</MenuItem>
							{/* Edit */}
							<MenuItem
								disabled={menuValues.type === "multiCollection"}
								onClick={() => {
									onEdit(menuValues);
									setMenuRef(null);
								}}
							>
								<Icon fontSize="medium">edit</Icon>&nbsp;{t("SETTINGS.edit")}
							</MenuItem>
							{/* Delete */}
							<ConfirmDialogButton
								onConfirm={(e, values) => {
									handleDeleteFolder(values);
									setMenuRef(null);
								}}
								component={
									<MenuItem style={{ color: "red" }}>
										<Icon fontSize="medium">delete</Icon>&nbsp;{t("SETTINGS.delete")}
									</MenuItem>
								}
								rowValues={menuValues}
								displayAttribute="name"
							/>

							<Divider />
							{/* Actions */}
							<MenuItem disabled style={{ opacity: 1 }}>
								<MDTypography variant="caption">{t("SETTINGS.actions")}</MDTypography>
							</MenuItem>

							{/* Translate name */}
							<MenuItem
								onClick={e => {
									setLanguageMenuAnchorEl({
										currentTarget: e.currentTarget,
										cb: code => {
											setMenuRef(null);
											translateCategoryName([menuValues._id], code);
										}
									});
								}}
							>
								<Icon fontSize="medium">translate</Icon>
								{t("ASSETS.THEME.ACTIONS.translateCategoryName")}
							</MenuItem>

							{/* Translate content */}
							<MenuItem
								onClick={e => {
									setLanguageMenuAnchorEl({
										currentTarget: e.currentTarget,
										cb: code => {
											setMenuRef(null);
											translateQuestions([menuValues.code], code);
										}
									});
								}}
							>
								<Icon fontSize="medium">translate</Icon>
								{t("ASSETS.THEME.ACTIONS.translateAllQR")}
							</MenuItem>
							{/* Train category dialog */}
							<ConfirmDialogButton
								onConfirm={(e, values) => {
									const id = v4();

									// Display a message to the user
									dispatch(
										display({
											id,
											message: t("ASSETS.TRAIN.starting"),
											type: "loading"
										})
									);

									startCategoryTraining([menuValues.code], "category", id);
								}}
								component={
									<MenuItem>
										<Icon fontSize="medium" sx={{ mr: 1 }}>
											model_training
										</Icon>
										{t("ASSETS.THEME.ACTIONS.trainQR")}
									</MenuItem>
								}
								title={t("ASSETS.THEME.startTrainingCategoryTitle")}
								content={t("ASSETS.THEME.startTrainingCategoryDescription")}
							/>

							{/* Untrain category dialog */}
							<ConfirmDialogButton
								onConfirm={(e, values) => {
									dispatch(
										display({
											message: t("ASSETS.THEME.questionsUntrainingStarted"),
											type: "info"
										})
									);

									startCategoryUntraining(
										[menuValues.code],
										"category",
										() => {
											dispatch(
												display({
													message: t("ASSETS.THEME.questionsUntrainingFinish"),
													type: "success"
												})
											);
										},
										() => {
											dispatch(
												display({
													message: t("ASSETS.THEME.untrainingError"),
													type: "error"
												})
											);
										}
									);
								}}
								component={
									<MenuItem style={{ color: "red" }}>
										<Icon fontSize="medium" sx={{ mr: 1 }}>
											model_training
										</Icon>
										{t("ASSETS.THEME.ACTIONS.untrainQR")}
									</MenuItem>
								}
								title={t("ASSETS.THEME.startUntrainingCategoryTitle")}
								content={t("ASSETS.THEME.startUntrainingCategoryDescription")}
							/>
						</Menu>
					</MDBox>
				</CardContent>
			</Card>
		</MDBox>
	);
};

export default ThemeCard;
